import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteField,
  increment,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { StandardIngredient } from '../types/ingredients';

// -----------------------------------------------------------------------------
// Best-effort client-side dual-write for `ingredientCatalogs/{lang}`.
// Mirrors the auth-backend service so the admin UI keeps the aggregated doc
// in sync even when it writes to standardIngredients via the Web SDK directly
// (status edits, alias edits, etc.).
//
// All public functions catch and log their own errors: a failed catalog
// write must never block the user's primary save action. The admin
// "Rebuild catalog" button is the recovery path if the two stores drift.
// -----------------------------------------------------------------------------

const CATALOG_COLLECTION = 'ingredientCatalogs';
export const SUPPORTED_LANGUAGES: ReadonlyArray<string> = ['fr', 'en'];
const INGREDIENT_CATALOG_MAX_ALIASES_PER_ITEM = 20;

function normalizeLanguage(lang: string | undefined | null): string {
  return String(lang || '').toLowerCase();
}

function isSupportedLanguage(lang: string | undefined | null): boolean {
  return SUPPORTED_LANGUAGES.includes(normalizeLanguage(lang));
}

function catalogDocRef(language: string) {
  return doc(db, CATALOG_COLLECTION, normalizeLanguage(language));
}

interface CatalogItem {
  name: string;
  aliases: string[];
  category: string;
  preferredUnit: string;
}

interface Projected {
  language: string;
  entry: CatalogItem;
}

function projectAliases(aliases: string[] | undefined): string[] {
  if (!Array.isArray(aliases)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const alias of aliases) {
    const trimmed = String(alias || '').trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
    if (result.length >= INGREDIENT_CATALOG_MAX_ALIASES_PER_ITEM) break;
  }
  return result;
}

/** Project a StandardIngredient into the minimal catalog entry. Returns null
 *  if the ingredient should NOT appear in the catalog (pending, missing name,
 *  unsupported language). */
export function projectIngredientForCatalog(
  ingredient: Partial<StandardIngredient> & { id?: string }
): Projected | null {
  if (!ingredient) return null;
  const status = ingredient.status || 'validated';
  if (status === 'pending') return null;

  const name = String(ingredient.name || '').trim();
  if (!name) return null;

  const language = normalizeLanguage(ingredient.language);
  if (!isSupportedLanguage(language)) return null;

  return {
    language,
    entry: {
      name,
      aliases: projectAliases(ingredient.aliases),
      category: ingredient.category || 'other',
      preferredUnit: ingredient.preferredUnit || 'piece',
    },
  };
}

async function upsertEntry(
  ingredientId: string,
  entry: CatalogItem,
  language: string
): Promise<void> {
  const ref = catalogDocRef(language);
  const snap = await getDoc(ref);
  const nowIso = new Date().toISOString();

  if (snap.exists()) {
    const previousItems = (snap.data()?.items ?? {}) as Record<string, unknown>;
    const isNewKey = !(ingredientId in previousItems);
    await updateDoc(ref, {
      [`items.${ingredientId}`]: entry,
      version: increment(1),
      updatedAt: nowIso,
      language: normalizeLanguage(language),
      ...(isNewKey ? { size: increment(1) } : {}),
    });
  } else {
    await setDoc(
      ref,
      {
        language: normalizeLanguage(language),
        version: 1,
        size: 1,
        updatedAt: nowIso,
        items: { [ingredientId]: entry },
      },
      { merge: true }
    );
  }
}

async function deleteEntry(ingredientId: string, language: string): Promise<boolean> {
  if (!isSupportedLanguage(language)) return false;
  const ref = catalogDocRef(language);
  const snap = await getDoc(ref);
  if (!snap.exists()) return false;
  const items = (snap.data()?.items ?? {}) as Record<string, unknown>;
  if (!(ingredientId in items)) return false;
  await updateDoc(ref, {
    [`items.${ingredientId}`]: deleteField(),
    version: increment(1),
    updatedAt: new Date().toISOString(),
    size: increment(-1),
  });
  return true;
}

async function deleteEntryFromAll(ingredientId: string): Promise<void> {
  await Promise.all(
    SUPPORTED_LANGUAGES.map((lang) =>
      deleteEntry(ingredientId, lang).catch((err) => {
        console.warn(`[catalog-sync] delete from ${lang} failed for ${ingredientId}:`, err);
        return false;
      })
    )
  );
}

/** Synchronize the aggregated catalog from a freshly-written
 *  standardIngredients document. Pass `previousLanguage` when an update
 *  may have changed the source language so we can clean up the stale
 *  entry in the previous doc. */
export async function syncCatalogForIngredient(
  ingredientId: string,
  after: Partial<StandardIngredient>,
  options: { previousLanguage?: string } = {}
): Promise<void> {
  // serverTimestamp is unused here; updateDoc receives a plain ISO string.
  // Re-exporting from the module guard so future authors don't break the API.
  void serverTimestamp;

  try {
    const projected = projectIngredientForCatalog(after);

    if (
      options.previousLanguage &&
      isSupportedLanguage(options.previousLanguage) &&
      (!projected || projected.language !== normalizeLanguage(options.previousLanguage))
    ) {
      await deleteEntry(ingredientId, options.previousLanguage).catch(() => false);
    }

    if (!projected) {
      await deleteEntryFromAll(ingredientId);
      return;
    }

    await upsertEntry(ingredientId, projected.entry, projected.language);
  } catch (err) {
    console.warn(`[catalog-sync] sync failed for ${ingredientId}:`, err);
  }
}

/** Remove the entry for an ingredient that has just been deleted. Uses the
 *  known language if available; otherwise fan-out to every supported one. */
export async function removeFromCatalog(
  ingredientId: string,
  language?: string
): Promise<void> {
  try {
    if (language && isSupportedLanguage(language)) {
      await deleteEntry(ingredientId, language).catch(() => false);
    } else {
      await deleteEntryFromAll(ingredientId);
    }
  } catch (err) {
    console.warn(`[catalog-sync] remove failed for ${ingredientId}:`, err);
  }
}
